import React from 'react';

const BLANK_CUSTOM_FIELD_VALUE = '—';

const formatPhone = (raw) => {
  if (!raw) return '--';
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return raw;
};

const formatDate = (raw) => {
  if (!raw) return '--';
  const parts = raw.split('-');
  if (parts.length !== 3) return raw;
  const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const monthIndex = parseInt(parts[1], 10) - 1;
  if (monthIndex < 0 || monthIndex > 11) return raw;
  return `${months[monthIndex]} ${parseInt(parts[2], 10)}, ${parts[0]}`;
};

const formatTime = (raw) => {
  if (!raw) return '--';
  const parts = raw.split(':');
  if (parts.length < 2) return raw;
  let h = parseInt(parts[0], 10);
  const m = parts[1];
  const ampm = h >= 12 ? 'PM' : 'AM';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${m} ${ampm}`;
};

const formatTimestamp = (raw) => {
  if (!raw) return '--';
  const [datePart, timePart] = raw.split(' ');
  if (!datePart) return raw;
  const date = formatDate(datePart);
  if (!timePart) return date;
  const time = formatTime(timePart);
  return `${date} at ${time}`;
};

export { formatPhone };

export const formatCustomFieldDisplayValue = (cfv) => {
  const type = cfv.type;
  const val = cfv.value;

  if (type === 'CustomFieldValues::Address') {
    return [
      val?.address1,
      val?.address2,
      val?.city,
      val?.state,
      val?.zip,
      val?.country,
    ]
      .filter(Boolean)
      .join(', ') || '--';
  }

  if (type === 'CustomFieldValues::Phone') return formatPhone(val);
  if (type === 'CustomFieldValues::Date') return formatDate(val);
  if (type === 'CustomFieldValues::Time') return formatTime(val);
  if (type === 'CustomFieldValues::Timestamp') return formatTimestamp(val);

  if (type === 'CustomFieldValues::Checkbox') {
    return val === true || val === 'true' ? 'Yes' : 'No';
  }

  return val || '--';
};

export const renderCustomFieldDisplayValue = (cfv, teamId) => {
  const type = cfv.type;
  const val = cfv.value;

  if (type === 'CustomFieldValues::Association' && val) {
    const label = cfv.association_label || val;
    const associationType = cfv.custom_field?.association_type;
    const path = associationType === 'Confirm' ? 'confirms' : 'crm/fans';
    if (teamId) {
      return <a href={`/teams/${teamId}/${path}/${val}`}>{label}</a>;
    }
    return label;
  }

  if (type === 'CustomFieldValues::Email' && val) {
    return <a href={`mailto:${val}`}>{val}</a>;
  }

  if (type === 'CustomFieldValues::Url' && val) {
    const href = val.match(/^https?:\/\//) ? val : `https://${val}`;
    return <a href={href} target="_blank" rel="noopener noreferrer">{val}</a>;
  }

  if (type === 'CustomFieldValues::File') {
    const url = cfv.uploaded_file_url;
    const fileName = val || 'Download';
    if (url) {
      return (
        <a href={url} target="_blank" rel="noopener noreferrer">
          <i className="fal fa-download mr-1" aria-hidden="true" />
          {fileName}
        </a>
      );
    }
    return fileName || '--';
  }

  return formatCustomFieldDisplayValue(cfv);
};

const formatCustomFieldAddressLines = (value) => {
  const cityStateZip = [
    [value?.city, value?.state].filter(Boolean).join(', '),
    value?.zip,
  ].filter(Boolean).join(' ');

  return [value?.address1, value?.address2, cityStateZip, value?.country]
    .map((line) => (line || '').trim())
    .filter(Boolean);
};

const customFieldValueIsBlank = (cfv) => {
  if (!cfv) return true;
  if (cfv.type === 'CustomFieldValues::Address') return formatCustomFieldAddressLines(cfv.value).length === 0;
  if (Array.isArray(cfv.value)) return cfv.value.length === 0;
  return cfv.value == null || String(cfv.value).trim() === '';
};

export const customFieldDisplayRows = (customFieldDefinitions, customFieldValues) => (
  (customFieldDefinitions || [])
    .filter((definition) => definition.enabled !== false)
    .map((definition) => ({
      definition,
      values: (customFieldValues || [])
        .filter((cfv) => cfv.custom_field_id === definition.id)
        .filter((cfv) => !customFieldValueIsBlank(cfv)),
    }))
);

export const renderCustomFieldRowValue = ({ definition, values }, teamId) => {
  if (values.length === 0) return BLANK_CUSTOM_FIELD_VALUE;

  if (definition.type === 'CustomFields::Address') {
    return values.flatMap((cfv) => (
      formatCustomFieldAddressLines(cfv.value).map((line, lineIndex) => (
        <div key={`${cfv.id || definition.id}-${lineIndex}`}>{line}</div>
      ))
    ));
  }

  return values.map((cfv, index) => (
    <div key={cfv.id || `${definition.id}-${index}`}>{renderCustomFieldDisplayValue(cfv, teamId)}</div>
  ));
};
